import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { BgContainer, TxtMessage } from 'common/CommonComponents';
import { LoadingSquare } from 'components/loading-square/LoadingSquare';
import logo from 'assets/images/Logo.webp';
import localizedStrings from 'localization';
import { AppDispatch } from 'redux/typings';
import { clearSignup, verifyCode } from 'modules/auth/actions';
import { LOADING_SCREEN } from 'routes';
import { AUTH } from 'routes';
import { AUTH_LOGIN } from 'modules/auth/routes';
import { VerifyPayload } from 'modules/auth/typings';
import useActionStatus from 'hooks/useActionStatus';
import { selectErrorMessage } from 'modules/error/selectors';
import { useSelector } from 'react-redux';

type dataParamsType = {
  verificationCode: string | null;
  name: string | null;
  email: string | null;
};

const EmailVerificationLoading = () => {
  const [hasVerified, setHasVerified] = useState(false);
  const [isVerifyingCode, hasVerifiedCode, hasRejectedCode] = useActionStatus(verifyCode);
  const dispatch = useDispatch<AppDispatch>();
  const { search } = useLocation();
  const history = useHistory();
  const errorMessageCode = useSelector(selectErrorMessage);

  const dataParams: dataParamsType = useMemo(() => {
    const urlParams = new URLSearchParams(search);
    return {
      verificationCode: urlParams.get('verificationCode'),
      name: urlParams.get('name'),
      email: urlParams.get('email'),
    };
  }, [search]);

  const isDataParamsExist = useMemo(
    () =>
      Boolean(dataParams.verificationCode) &&
      Boolean(dataParams.email) &&
      Boolean(dataParams.name),
    [dataParams],
  );

  // Auto-verify when component mounts with valid params
  useEffect(() => {
    if (isDataParamsExist && !hasVerified && !isVerifyingCode) {
      setHasVerified(true);
      const verifyPayload: VerifyPayload = {
        email: dataParams.email!.replaceAll(' ', '+'),
        verificationCode: dataParams.verificationCode!,
      };
      dispatch(verifyCode(verifyPayload));
    }
  }, [isDataParamsExist, hasVerified, isVerifyingCode, dataParams, dispatch]);

  // Handle successful verification
  useEffect(() => {
    if (hasVerified && hasVerifiedCode) {
      // Clear signup data and redirect to loading screen (which will handle onboarding)
      dispatch(clearSignup());
      history.push(LOADING_SCREEN);
    }
  }, [hasVerified, hasVerifiedCode, dispatch, history]);

  // Handle errors - check both errorMessageCode and hasRejectedCode
  useEffect(() => {
    if (hasVerified && hasRejectedCode && !hasVerifiedCode) {
      // Small delay to ensure error message is set in the store
      const timeoutId = setTimeout(() => {
        let errorMessage = '';
        let shouldRedirect = true;

        if (errorMessageCode === 'errorVerificationUser') {
          // User already verified - redirect to login with toast
          errorMessage = localizedStrings.errorVerificationUser;
        } else if (errorMessageCode === 'errorVerificationCode') {
          // Invalid verification code
          errorMessage = localizedStrings.errorVerificationCode;
        } else if (errorMessageCode === 'errorBackendDown' || errorMessageCode === 'errorServerDown') {
          // Backend/server down - show error and redirect
          errorMessage = localizedStrings.emailVerificationError || 
            'An error occurred during email verification. Please contact an administrator.';
        } else {
          // Other errors - generic error message
          errorMessage = localizedStrings.emailVerificationError || 
            'An error occurred during email verification. Please contact an administrator.';
        }

        // Show toast error
        if (errorMessage) {
          toast.error(errorMessage, {
            position: 'top-center',
            autoClose: errorMessageCode === 'errorBackendDown' || errorMessageCode === 'errorServerDown' ? 5000 : 3000,
          });
        }

        // Clear signup data and redirect to login
        if (shouldRedirect) {
          dispatch(clearSignup());
          setTimeout(() => {
            history.push(AUTH + AUTH_LOGIN);
          }, 500);
        }
      }, 200);

      return () => clearTimeout(timeoutId);
    }
  }, [hasVerified, hasRejectedCode, hasVerifiedCode, errorMessageCode, dispatch, history]);

  // If no valid params, redirect to login
  useEffect(() => {
    if (!isDataParamsExist && !isVerifyingCode) {
      toast.error(
        localizedStrings.emailVerificationInvalidLink ||
          'Invalid verification link. Please contact an administrator.',
        {
          position: 'top-center',
          autoClose: 3000,
        },
      );
      dispatch(clearSignup());
      setTimeout(() => {
        history.push(AUTH + AUTH_LOGIN);
      }, 500);
    }
  }, [isDataParamsExist, isVerifyingCode, dispatch, history]);

  return (
    <BgContainer>
      <img src={logo} alt="brand logo" />
      <TxtMessage>
        {localizedStrings.emailVerificationLoading ||
          'Verifying your email... Please wait.'}
      </TxtMessage>
      <LoadingSquare />
    </BgContainer>
  );
};

export default EmailVerificationLoading;
